import { MED_FLOW, MED_SKU } from "src/utils/medSku";
import {
  BRANDED_PLAN_INELIGIBLE_HEALTH_RISKS,
  CLINICAL_INELIGIBLE_HEALTH_RISKS,
  HRT_INELIGIBLE_HEALTH_RISKS,
  MED_BMI_INCREASED_THRESHOLD,
  MED_BMI_SPECIAL_LOW_MIN,
  MED_BMI_THRESHOLD_BRANDED,
  MED_BMI_THRESHOLD_ORALS,
  MED_BMI_THRESHOLD_PROACTIVE_HEALTH,
  MED_BMI_THRESHOLD_SEMAGLUTIDE_LIRAGLUTIDE,
  ORALS_PLAN_INELIGIBLE_HEALTH_RISKS,
  SEMAGLUTIDE_LIRAGLUTIDE_PLAN_INELIGIBLE_HEALTH_RISKS,
} from "src/components/refactored-survey/question-sets/insurance-survey-questions/utils/insuranceConstants";
import { appConfig } from "src/config";
import {
  isAgeRangeStatusNoomClinicalEligible,
  isEatingDisorderStatusNoomClinicalEligible,
  isFlowEligibleForClinicalProducts,
  isFlowEligibleForHRTProducts,
  isHRTPatchEnabled,
  isInAppMedUpgrade,
  isLongevityMicrodoseEnabled,
  isMicrodoseTirzepatideFeatureEnabled,
  isPregnantStatusHealthCheckEligible,
  isQualifiedForPersonalizedDosing,
  isBrandedAsyncEnabled,
  isBrandedWegovyPillFlowEnabled,
  isBrandedZepboundVialFlowEnabled,
  isTirzepatideFeatureEnabled,
  hasMedicareInsurance,
  shouldApplyVendorEngineGate,
} from "@utils/userSegment/features";
import { getSubdivision } from "./meristemContext";
import zipState from "zip-state";
import { calculateBMIImperial, calculateBMIMetric } from "src/primitive/bmi";
import { healthRiskOptions } from "src/pages/survey/currentHealthRiskMedical";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import { hrtHealthRiskOptions } from "src/pages/hrt-survey/currentHealthRiskHRT";
import {
  isB2BUpsellClinicalRoute,
  isCalorieClinicalRoute,
  isFreemiumHealthRoute,
  isHealthRoute,
  isHRTRoute,
  isInsuranceCheckerRoute,
} from "@utils/userSegment";
import getStore from "./redux/store";
import {
  BRANDED_ASYNC_PRODUCT_NAME,
  BRANDED_SYNC_PRODUCT_NAME,
} from "./medProductNames";
import { hasNoClinicalVendorConflict } from "./redux/slices/productEligibility";
import {
  NOOM_MED_ORALS_PURCHASE_EVENT_NAME,
  NOOM_MED_COMPOUNDED_PURCHASE_EVENT_NAME,
  NOOM_MED_HRT_CREAM_PURCHASE_EVENT_NAME,
  NOOM_MED_HRT_PATCH_PURCHASE_EVENT_NAME,
  NOOM_MED_BRANDED_PURCHASE_EVENT_NAME,
  NOOM_MED_MDS_PURCHASE_EVENT_NAME,
  NOOM_MED_COMPOUNDED_TIRZEPATIDE_PURCHASE_EVENT_NAME,
  NOOM_MED_COMPOUNDED_MICRODOSE_TIRZEPATIDE_PURCHASE_EVENT_NAME,
  NOOM_MED_MDS_BIOMARKER_PURCHASE_EVENT_NAME,
  NOOM_MED_LIRAGLUTIDE_PURCHASE_EVENT_NAME,
} from "./constants";

import { NO_US_ZIPCODE } from "src/utils/normalizePostalCode";

import { NO_US_STATE } from "src/utils/usState";

export { MED_FLOW, MED_SKU };

export const BRANDED_MED_SKUS: MED_SKU[] = [
  MED_SKU.BRANDED,
  MED_SKU.BRANDED_ZEPBOUND_VIAL,
  MED_SKU.BRANDED_WEGOVY_PILL,
];

export const MED_WEIGHT_LOSS_PLAN_SKUS: MED_SKU[] = [
  MED_SKU.BRANDED,
  MED_SKU.BRANDED_ZEPBOUND_VIAL,
  MED_SKU.BRANDED_WEGOVY_PILL,
  MED_SKU.COMPOUNDED,
  MED_SKU.COMPOUNDED_TIRZEPATIDE,
  MED_SKU.COMPOUNDED_MICRODOSE,
  MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
  MED_SKU.ORALS_METFORMIN,
  MED_SKU.GENERIC_LIRAGLUTIDE,
];

export const MED_DRIP_PROMO_ELIGIBLE_PLAN_SKUS: MED_SKU[] = [
  MED_SKU.COMPOUNDED,
  MED_SKU.COMPOUNDED_TIRZEPATIDE,
  MED_SKU.COMPOUNDED_MICRODOSE,
  MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
];

export const MED_GLP1_WEIGHT_LOSS_PLAN_SKUS: MED_SKU[] = [
  MED_SKU.BRANDED,
  MED_SKU.BRANDED_ZEPBOUND_VIAL,
  MED_SKU.BRANDED_WEGOVY_PILL,
  MED_SKU.COMPOUNDED,
  MED_SKU.COMPOUNDED_TIRZEPATIDE,
  MED_SKU.COMPOUNDED_MICRODOSE,
  MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
  MED_SKU.GENERIC_LIRAGLUTIDE,
];

export const MED_SKU_TO_FLOWER_NAME: Record<MED_SKU, string> = {
  [MED_SKU.ORALS_METFORMIN]: NOOM_MED_ORALS_PURCHASE_EVENT_NAME,
  [MED_SKU.COMPOUNDED]: NOOM_MED_COMPOUNDED_PURCHASE_EVENT_NAME,
  [MED_SKU.HRT_CREAM]: NOOM_MED_HRT_CREAM_PURCHASE_EVENT_NAME,
  [MED_SKU.HRT_PATCH]: NOOM_MED_HRT_PATCH_PURCHASE_EVENT_NAME,
  [MED_SKU.BRANDED]: NOOM_MED_BRANDED_PURCHASE_EVENT_NAME,
  [MED_SKU.BRANDED_ZEPBOUND_VIAL]: NOOM_MED_BRANDED_PURCHASE_EVENT_NAME,
  [MED_SKU.BRANDED_WEGOVY_PILL]: NOOM_MED_BRANDED_PURCHASE_EVENT_NAME,
  [MED_SKU.COMPOUNDED_MICRODOSE]: NOOM_MED_MDS_PURCHASE_EVENT_NAME,
  [MED_SKU.COMPOUNDED_TIRZEPATIDE]:
    NOOM_MED_COMPOUNDED_TIRZEPATIDE_PURCHASE_EVENT_NAME,
  [MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE]:
    NOOM_MED_COMPOUNDED_MICRODOSE_TIRZEPATIDE_PURCHASE_EVENT_NAME,
  [MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER]:
    NOOM_MED_MDS_BIOMARKER_PURCHASE_EVENT_NAME,
  [MED_SKU.GENERIC_LIRAGLUTIDE]: NOOM_MED_LIRAGLUTIDE_PURCHASE_EVENT_NAME,
};

export enum NON_MED_PLAN {
  NOOM_WEIGHT = "NOOM_WEIGHT",
  FREEMIUM = "FREEMIUM",
}

export type HealthPlan = MED_SKU | NON_MED_PLAN;

type UserInfo = {
  bmi: number;
  highestBMI: number;
  previousMedications: string[];
  whichMedication: string[];
  medicalHealthRisks: HealthRiskOption[];
  hrtHealthRisks: HealthRiskOption[];
  state: string;
  priorGLP1?: string[];
};

export const ELIGIBLE_SKUS_FOR_SWITCH = [
  MED_SKU.COMPOUNDED,
  MED_SKU.ORALS_METFORMIN,
];

type HealthRiskOption =
  | (typeof healthRiskOptions)[number]
  | (typeof hrtHealthRiskOptions)[number];

export type NamedEligibilityCheck =
  | "isQualifiedForPD"
  | "isNotEligibleForCompoundedSemaglutide"
  | "hasEligibleBrandedVariant";

type NamedCheckContext = {
  state: string;
  checkState: boolean;
};

type SkuSpecificAddressableEligibilityChecks = {
  [K in NamedEligibilityCheck]?: (context: NamedCheckContext) => boolean;
};

type BmiCriteria = {
  threshold?: number;
  increasedThreshold?: number;
  increasedThresholdStates?: string[];
};

type EligibilityInfo = {
  flow: MED_FLOW;
  bmiCriteria?: BmiCriteria;
  bmiRules?: Array<(context: BmiContext) => boolean>;
  ineligibleHealthRisks: HealthRiskOption[];
  ineligibleStates?: string[];
  flowEligibilityCheck?: () => boolean;
  skuSpecificAddressableEligibilityChecks?: SkuSpecificAddressableEligibilityChecks;
};

type EligibilityCheckOptions = {
  checkBMI: boolean;
  checkHealthRisks: boolean;
  checkState: boolean;
  checkFlow: boolean;
  disabledNamedChecks?: NamedEligibilityCheck[];
  requirePreviousMedicationsAnswer?: boolean;
  requireHighestBMIAnswer?: boolean;
};

type BmiContext = {
  currentBMI: number;
  highestBMI: number;
  previousMedications?: string[];
  whichMedication?: string[];
  priorGLP1?: string[];
  state: string;
  effectiveThreshold: number;
  lowMin: number;
  requirePreviousMedicationsAnswer?: boolean;
  requireHighestBMIAnswer?: boolean;
};

export function computeEffectiveThreshold(
  criteria: BmiCriteria | undefined,
  state: string,
  checkState = true
) {
  const { threshold, increasedThreshold, increasedThresholdStates } = criteria;
  return increasedThreshold &&
    checkState &&
    increasedThresholdStates?.includes(state)
    ? increasedThreshold
    : threshold;
}

const atOrAboveEffectiveThreshold = ({
  currentBMI,
  effectiveThreshold,
}: BmiContext) => currentBMI >= effectiveThreshold;

const isInLowBmiWindow = (
  currentBMI: number,
  lowMin: number,
  threshold: number
) => currentBMI >= lowMin && currentBMI < threshold;

const didTakeWeightLossMeds = (
  previousMedications?: string[],
  whichMedication?: string[]
) => {
  const tookMeds =
    !!previousMedications &&
    (previousMedications[0] === "yes" ||
      previousMedications[0] === "takingWeightLossMed");

  const tookGLP1Medication =
    !!whichMedication &&
    whichMedication.some((med) =>
      [
        "wegovy",
        "ozempic",
        "compoundedSemaglutide",
        "zepbound",
        "mounjaro",
        "compoundedTirzepatide",
      ].includes(med)
    );
  return tookMeds && tookGLP1Medication;
};

const lowBmiWithPriorGLP1Meds = ({
  currentBMI,
  highestBMI,
  previousMedications,
  whichMedication,
  priorGLP1,
  effectiveThreshold,
  lowMin,
  requirePreviousMedicationsAnswer,
  requireHighestBMIAnswer,
}: BmiContext) => {
  if (!currentBMI) return false;
  if (!isInLowBmiWindow(currentBMI, lowMin, effectiveThreshold)) return false;
  if (requireHighestBMIAnswer) {
    if (!highestBMI || highestBMI < effectiveThreshold) return false;
  }
  if (!requirePreviousMedicationsAnswer) {
    // For health route: block full-dose GLP-1 if BMI < threshold
    // For other routes: allow (return true)
    if (isHealthRoute() || isFreemiumHealthRoute()) {
      return false; // Block low BMI on health route
    }
    return true;
  }
  if (priorGLP1?.length) {
    return priorGLP1[0] === "yes";
  }
  return didTakeWeightLossMeds(previousMedications, whichMedication);
};

const compoundedEligibilityCriteria = {
  bmiCriteria: {
    threshold: MED_BMI_THRESHOLD_SEMAGLUTIDE_LIRAGLUTIDE,
  },
  bmiRules: [atOrAboveEffectiveThreshold, lowBmiWithPriorGLP1Meds],
  ineligibleHealthRisks: [
    ...SEMAGLUTIDE_LIRAGLUTIDE_PLAN_INELIGIBLE_HEALTH_RISKS,
    ...CLINICAL_INELIGIBLE_HEALTH_RISKS,
  ],
  ineligibleStates: appConfig.COMPOUNDED_PLAN_INELIGIBLE_US_STATES,

  flowEligibilityCheck: () => isFlowEligibleForClinicalProducts(),
  flow: MED_FLOW.CLINICAL,
  skuSpecificAddressableEligibilityChecks: {
    isQualifiedForPD: () => isQualifiedForPersonalizedDosing(),
  },
};

const isNotMedicare = () => !hasMedicareInsurance();

/**
 * Fulfillment variants for the branded SKUs (BRANDED, BRANDED_ZEPBOUND_VIAL,
 * BRANDED_WEGOVY_PILL). The same medication can be offered through a
 * synchronous (video visit) or asynchronous pathway; each variant layers its
 * own conditions on top of the SKU-level eligibility in ELIGIBILITY_INFO.
 */
export enum BRANDED_VARIANT {
  SYNC = "SYNC",
  ASYNC = "ASYNC",
}

const BRANDED_VARIANT_TO_PRODUCT_NAME: Record<BRANDED_VARIANT, string> = {
  [BRANDED_VARIANT.SYNC]: BRANDED_SYNC_PRODUCT_NAME,
  [BRANDED_VARIANT.ASYNC]: BRANDED_ASYNC_PRODUCT_NAME,
};

const passesVendorEngineGate = (variant: BRANDED_VARIANT) => () =>
  !shouldApplyVendorEngineGate() ||
  hasNoClinicalVendorConflict(BRANDED_VARIANT_TO_PRODUCT_NAME[variant]);

type BrandedVariantEligibilityInfo = {
  /** Blocked states in addition to the SKU's own ineligibleStates. */
  additionalIneligibleStates?: string[];
  enabledCheck?: () => boolean;
  userChecks?: Array<() => boolean>;
};

const BRANDED_VARIANT_ELIGIBILITY: Record<
  BRANDED_VARIANT,
  BrandedVariantEligibilityInfo
> = {
  [BRANDED_VARIANT.SYNC]: {
    userChecks: [isNotMedicare, passesVendorEngineGate(BRANDED_VARIANT.SYNC)],
  },
  [BRANDED_VARIANT.ASYNC]: {
    // Async is offered on clinical routes and the in-app med upgrade flow
    // (see isBrandedAsyncEnabled).
    enabledCheck: isBrandedAsyncEnabled,
    additionalIneligibleStates: appConfig.ASYNC_CASH_INELIGIBLE_US_STATES,
    userChecks: [passesVendorEngineGate(BRANDED_VARIANT.ASYNC)],
  },
};

/** Preferred variant first: offer sync when possible, fall back to async. */
const BRANDED_VARIANT_PRIORITY = [BRANDED_VARIANT.SYNC, BRANDED_VARIANT.ASYNC];

export function getEligibleBrandedVariants(
  state: string = getMedState(),
  checkState = true
): BRANDED_VARIANT[] {
  return BRANDED_VARIANT_PRIORITY.filter((variant) => {
    const info = BRANDED_VARIANT_ELIGIBILITY[variant];
    if (info.enabledCheck && !info.enabledCheck()) {
      return false;
    }
    if (checkState && info.additionalIneligibleStates?.includes(state)) {
      return false;
    }
    return (info.userChecks ?? []).every((check) => check());
  });
}

const hasEligibleBrandedVariant = ({ state, checkState }: NamedCheckContext) =>
  getEligibleBrandedVariants(state, checkState).length > 0;

const ELIGIBILITY_INFO: Record<MED_SKU, EligibilityInfo> = {
  [MED_SKU.BRANDED_ZEPBOUND_VIAL]: {
    bmiCriteria: {
      threshold: MED_BMI_THRESHOLD_BRANDED,
      increasedThreshold: MED_BMI_INCREASED_THRESHOLD,
      increasedThresholdStates: appConfig.GLP1_INCREASED_BMI_US_STATES,
    },
    bmiRules: [atOrAboveEffectiveThreshold],
    ineligibleHealthRisks: [
      ...BRANDED_PLAN_INELIGIBLE_HEALTH_RISKS,
      ...CLINICAL_INELIGIBLE_HEALTH_RISKS,
    ],
    ineligibleStates: appConfig.BRANDED_PLAN_INELIGIBLE_US_STATES,
    flowEligibilityCheck: () =>
      isFlowEligibleForClinicalProducts() &&
      isBrandedZepboundVialFlowEnabled() &&
      !isInsuranceCheckerRoute(),

    flow: MED_FLOW.CLINICAL,
    skuSpecificAddressableEligibilityChecks: {
      hasEligibleBrandedVariant,
    },
  },
  [MED_SKU.BRANDED_WEGOVY_PILL]: {
    bmiCriteria: {
      threshold: MED_BMI_THRESHOLD_BRANDED,
      increasedThreshold: MED_BMI_INCREASED_THRESHOLD,
      increasedThresholdStates: appConfig.GLP1_INCREASED_BMI_US_STATES,
    },
    bmiRules: [atOrAboveEffectiveThreshold],
    ineligibleHealthRisks: [
      ...BRANDED_PLAN_INELIGIBLE_HEALTH_RISKS,
      ...CLINICAL_INELIGIBLE_HEALTH_RISKS,
    ],
    ineligibleStates: appConfig.BRANDED_PLAN_INELIGIBLE_US_STATES,
    flowEligibilityCheck: () =>
      isFlowEligibleForClinicalProducts() &&
      isBrandedWegovyPillFlowEnabled() &&
      !isInsuranceCheckerRoute(),

    flow: MED_FLOW.CLINICAL,
    skuSpecificAddressableEligibilityChecks: {
      hasEligibleBrandedVariant,
    },
  },
  [MED_SKU.BRANDED]: {
    bmiCriteria: {
      threshold: MED_BMI_THRESHOLD_BRANDED,
      increasedThreshold: MED_BMI_INCREASED_THRESHOLD,
      increasedThresholdStates: appConfig.GLP1_INCREASED_BMI_US_STATES,
    },
    bmiRules: [atOrAboveEffectiveThreshold],
    ineligibleHealthRisks: [
      ...BRANDED_PLAN_INELIGIBLE_HEALTH_RISKS,
      ...CLINICAL_INELIGIBLE_HEALTH_RISKS,
    ],
    ineligibleStates: appConfig.BRANDED_PLAN_INELIGIBLE_US_STATES,

    flowEligibilityCheck: () => isFlowEligibleForClinicalProducts(),
    flow: MED_FLOW.CLINICAL,
    skuSpecificAddressableEligibilityChecks: {
      hasEligibleBrandedVariant,
    },
  },
  [MED_SKU.COMPOUNDED]: compoundedEligibilityCriteria,
  [MED_SKU.COMPOUNDED_TIRZEPATIDE]: {
    ...compoundedEligibilityCriteria,
    ineligibleStates: appConfig.TIRZEPATIDE_PLAN_INELIGIBLE_US_STATES,
    flowEligibilityCheck: () =>
      isFlowEligibleForClinicalProducts() && isTirzepatideFeatureEnabled(),
  },
  [MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE]: {
    ...compoundedEligibilityCriteria,
    ineligibleStates: appConfig.MICRODOSE_TIRZEPATIDE_PLAN_INELIGIBLE_US_STATES,
    flowEligibilityCheck: () =>
      isFlowEligibleForClinicalProducts() &&
      isMicrodoseTirzepatideFeatureEnabled(),
  },
  [MED_SKU.COMPOUNDED_MICRODOSE]: {
    ...compoundedEligibilityCriteria,
    ineligibleStates: appConfig.COMPOUNDED_MICRODOSE_PLAN_INELIGIBLE_US_STATES,
    flowEligibilityCheck: () => isFlowEligibleForClinicalProducts(),
  },
  [MED_SKU.GENERIC_LIRAGLUTIDE]: {
    bmiCriteria: {
      threshold: MED_BMI_THRESHOLD_SEMAGLUTIDE_LIRAGLUTIDE,
    },
    bmiRules: [atOrAboveEffectiveThreshold, lowBmiWithPriorGLP1Meds],
    ineligibleHealthRisks: [
      ...SEMAGLUTIDE_LIRAGLUTIDE_PLAN_INELIGIBLE_HEALTH_RISKS,
      ...CLINICAL_INELIGIBLE_HEALTH_RISKS,
    ],
    ineligibleStates: appConfig.LIRAGLUTIDE_PLAN_INELIGIBLE_US_STATES,

    flowEligibilityCheck: () =>
      isFlowEligibleForClinicalProducts() && !isInsuranceCheckerRoute(),
    flow: MED_FLOW.CLINICAL,
    skuSpecificAddressableEligibilityChecks: {
      isNotEligibleForCompoundedSemaglutide: () => {
        const eligibleProducts = getEligibleMedProductsBase({
          checkBMI: true,
          checkHealthRisks: true,
          checkState: true,
          checkFlow: true,
          requirePreviousMedicationsAnswer: true,
          requireHighestBMIAnswer: true,
          disabledNamedChecks: ["isNotEligibleForCompoundedSemaglutide"],
        });
        return !eligibleProducts.includes(MED_SKU.COMPOUNDED);
      },
    },
  },
  [MED_SKU.ORALS_METFORMIN]: {
    bmiCriteria: { threshold: MED_BMI_THRESHOLD_ORALS },
    bmiRules: [atOrAboveEffectiveThreshold],
    ineligibleHealthRisks: [
      ...ORALS_PLAN_INELIGIBLE_HEALTH_RISKS,
      ...CLINICAL_INELIGIBLE_HEALTH_RISKS,
    ],
    ineligibleStates: appConfig.ORALS_PLAN_INELIGIBLE_US_STATES,
    flowEligibilityCheck: () => isFlowEligibleForClinicalProducts(),
    flow: MED_FLOW.CLINICAL,
  },
  [MED_SKU.HRT_CREAM]: {
    ineligibleHealthRisks: HRT_INELIGIBLE_HEALTH_RISKS,
    flowEligibilityCheck: () => isFlowEligibleForHRTProducts(),
    ineligibleStates: appConfig.HRT_INELIGIBLE_US_STATES,
    flow: MED_FLOW.HRT,
  },
  [MED_SKU.HRT_PATCH]: {
    ineligibleHealthRisks: HRT_INELIGIBLE_HEALTH_RISKS,
    flowEligibilityCheck: () =>
      isHRTPatchEnabled() && isFlowEligibleForHRTProducts(),
    ineligibleStates: appConfig.HRT_INELIGIBLE_US_STATES,
    flow: MED_FLOW.HRT,
  },
  [MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER]: {
    bmiCriteria: {
      threshold: MED_BMI_THRESHOLD_PROACTIVE_HEALTH,
    },
    bmiRules: [atOrAboveEffectiveThreshold],
    ineligibleHealthRisks: [
      ...SEMAGLUTIDE_LIRAGLUTIDE_PLAN_INELIGIBLE_HEALTH_RISKS,
      ...CLINICAL_INELIGIBLE_HEALTH_RISKS,
    ],
    ineligibleStates: appConfig.LONGEVITY_MICRODOSE_PLAN_INELIGIBLE_US_STATES,
    flowEligibilityCheck: () =>
      isFlowEligibleForClinicalProducts() &&
      isLongevityMicrodoseEnabled() &&
      !(isInAppMedUpgrade() || isCalorieClinicalRoute()),
    flow: MED_FLOW.CLINICAL,
  },
};

function getBMIFromSurvey({
  heightFeet,
  heightInch,
  heightCm,
  weightField,
  weightKgField,
}: {
  heightFeet?: number;
  heightInch?: number;
  heightCm?: number;
  weightField?: number;
  weightKgField?: number;
}) {
  if (
    heightFeet !== undefined &&
    heightInch !== undefined &&
    weightField !== undefined
  ) {
    return calculateBMIImperial(heightFeet, heightInch, weightField);
  }
  if (heightCm !== undefined && weightKgField !== undefined) {
    return calculateBMIMetric(heightCm, weightKgField);
  }
  return NaN;
}

export function getBMI() {
  const { heightFeet, heightInch, weight, heightCm, weightKg } =
    getSurveyAnswers();

  return getBMIFromSurvey({
    heightFeet,
    heightInch,
    heightCm,
    weightField: weight,
    weightKgField: weightKg,
  });
}

export function getHighestBMI() {
  const { heightFeet, heightInch, highestWeight, heightCm, highestWeightKg } =
    getSurveyAnswers();

  return getBMIFromSurvey({
    heightFeet,
    heightInch,
    heightCm,
    weightField: highestWeight,
    weightKgField: highestWeightKg,
  });
}

export function getMedState() {
  const { zipcode, usState } = getSurveyAnswers();

  // A collected zipcode is the authoritative (real shipping address) source, so
  // it takes precedence over any handed-over usState. This preserves the exact
  // pre-existing behavior for v1-native users.
  // Note (Alex): Intially, we use the IP state as the source of truth for the state eligibility;
  // Once we collect the zipcode, we will only trust the state determined based on the zipcode
  if (zipcode === NO_US_ZIPCODE) {
    return zipcode;
  }
  if (zipcode !== undefined) {
    return zipState(zipcode);
  }

  // No zipcode collected (e.g. a user handed over from v2): fall back to the
  // canonical usState answer before the geo-IP subdivision.
  if (usState === NO_US_STATE) {
    return NO_US_ZIPCODE;
  }
  if (typeof usState === "string" && usState.length > 0) {
    return usState;
  }

  return getSubdivision();
}

export function hasIneligibleHealthRisks(
  healthRisks: HealthRiskOption[],
  ineligibleHealthRisks: HealthRiskOption[]
) {
  const ineligibleAnswers = healthRisks.filter((answer) =>
    ineligibleHealthRisks.includes(answer)
  );

  return !!ineligibleAnswers.length;
}

export function getMedicalHealthRisks(): HealthRiskOption[] {
  const { currentHealthRiskMedical } = getSurveyAnswers();

  return (currentHealthRiskMedical || []).filter(
    (el) => typeof el === "string"
  ) as HealthRiskOption[];
}

export function getHrtHealthRisks(): HealthRiskOption[] {
  const { currentHealthRiskHRT } = getSurveyAnswers();

  return currentHealthRiskHRT || [];
}

export function isSpecialLowBMIIneligibleCohort({
  currentBMI,
  highestBMI,
  previousMedications,
  whichMedication,
  bmiThreshold,
}: {
  currentBMI: number;
  highestBMI: number;
  previousMedications: string[];
  whichMedication: string[] | undefined;
  bmiThreshold: number;
}): boolean {
  return (
    !didTakeWeightLossMeds(previousMedications, whichMedication) &&
    currentBMI >= MED_BMI_SPECIAL_LOW_MIN &&
    currentBMI < bmiThreshold &&
    highestBMI >= bmiThreshold
  );
}

export function getMedBMIThreshold(considerUserLocation = false, currentBMI) {
  let eligibilityKeys: Array<keyof typeof ELIGIBILITY_INFO> = [];
  // Get the products that pass the extra check, this means we don't
  // take into account the orals bmi threshold if it's disabled
  eligibilityKeys = eligibilityKeys.concat(
    getEligibleMedProductsBase({
      checkBMI: false,
      checkHealthRisks: false,
      checkState: false,
      checkFlow: true,
      requirePreviousMedicationsAnswer: false,
      requireHighestBMIAnswer: false,

      disabledNamedChecks: [
        "isQualifiedForPD",
        "isNotEligibleForCompoundedSemaglutide",
      ],
    })
  );

  const userState = getMedState();
  const bmiThresholds = eligibilityKeys
    .map((key) => {
      const { bmiCriteria } = ELIGIBILITY_INFO[key];
      if (!bmiCriteria?.threshold) return undefined;
      return computeEffectiveThreshold(
        bmiCriteria,
        userState,
        considerUserLocation
      );
    })
    .filter((v): v is number => typeof v === "number");

  if (currentBMI <= MED_BMI_SPECIAL_LOW_MIN) {
    bmiThresholds.push(MED_BMI_SPECIAL_LOW_MIN);
  }

  return Math.min(...bmiThresholds);
}

export type EligibilityCheckName =
  | "bmi"
  | "healthRisk"
  | "state"
  | "flow"
  | "skuSpecific"
  | "saleItems";

function evaluateEligibilityInfo(
  eligibilityInfo: EligibilityInfo,
  sku: MED_SKU,
  userInfo: UserInfo,
  eligibilityCheckOptions: EligibilityCheckOptions
): Record<EligibilityCheckName, boolean> {
  const {
    bmiCriteria,
    ineligibleHealthRisks,
    ineligibleStates,
    flowEligibilityCheck,
    skuSpecificAddressableEligibilityChecks,
    flow,
  } = eligibilityInfo;
  const { medicalHealthRisks, hrtHealthRisks, state } = userInfo;
  const healthRisks =
    flow === MED_FLOW.HRT ? hrtHealthRisks : medicalHealthRisks;
  const {
    checkBMI,
    checkHealthRisks,
    checkState,
    disabledNamedChecks,
    checkFlow,
  } = eligibilityCheckOptions;

  let passesBmiCheck = true;
  if (checkBMI && eligibilityInfo.bmiRules?.length && bmiCriteria?.threshold) {
    const effectiveThreshold = computeEffectiveThreshold(
      bmiCriteria,
      state,
      checkState
    );

    const context: BmiContext = {
      currentBMI: userInfo.bmi,
      highestBMI: userInfo.highestBMI,
      previousMedications: userInfo.previousMedications,
      whichMedication: userInfo.whichMedication,
      priorGLP1: userInfo.priorGLP1,
      state,
      effectiveThreshold: effectiveThreshold as number,
      lowMin: MED_BMI_SPECIAL_LOW_MIN,
      requirePreviousMedicationsAnswer:
        eligibilityCheckOptions.requirePreviousMedicationsAnswer,
      requireHighestBMIAnswer: eligibilityCheckOptions.requireHighestBMIAnswer,
    };

    passesBmiCheck = eligibilityInfo.bmiRules.some((rule) => rule(context));
  }

  const passesHealthRiskCheck =
    checkHealthRisks && ineligibleHealthRisks
      ? !hasIneligibleHealthRisks(ineligibleHealthRisks, healthRisks)
      : true;
  let passesStateCheck = true;
  if (checkState) {
    if (state === NO_US_ZIPCODE) {
      passesStateCheck = false;
    } else if (ineligibleStates) {
      passesStateCheck = !ineligibleStates.includes(state);
    }
  }
  const passesExtraCheck =
    checkFlow && flowEligibilityCheck ? flowEligibilityCheck() : true;

  let passesSkuSpecificChecks = true;
  if (skuSpecificAddressableEligibilityChecks) {
    const allCheckNames = Object.keys(
      skuSpecificAddressableEligibilityChecks
    ) as NamedEligibilityCheck[];
    const checksToRun = disabledNamedChecks
      ? allCheckNames.filter((check) => !disabledNamedChecks.includes(check))
      : allCheckNames;
    passesSkuSpecificChecks = checksToRun.every((checkName) => {
      const checkFn = skuSpecificAddressableEligibilityChecks[checkName];
      return checkFn ? checkFn({ state, checkState }) : true;
    });
  }

  let passesEligibleSaleItemsCheck = true;
  if (isB2BUpsellClinicalRoute()) {
    const { eligibleSaleItems } = getStore().getState().recommendationContext;
    passesEligibleSaleItemsCheck = eligibleSaleItems?.some(
      (saleItem) => saleItem.medSku === sku
    );
  }

  return {
    bmi: passesBmiCheck,
    healthRisk: passesHealthRiskCheck,
    state: passesStateCheck,
    flow: passesExtraCheck,
    skuSpecific: passesSkuSpecificChecks,
    saleItems: !!passesEligibleSaleItemsCheck,
  };
}

function meetsEligibilityInfo(
  sku: MED_SKU,
  eligibilityInfo: EligibilityInfo,
  userInfo: UserInfo,
  eligibilityCheckOptions: EligibilityCheckOptions
) {
  return Object.values(
    evaluateEligibilityInfo(
      eligibilityInfo,
      sku,
      userInfo,
      eligibilityCheckOptions
    )
  ).every(Boolean);
}

function buildEligibilityUserInfo(stateOverwrite?: string): UserInfo {
  const { previousMedications, whichMedication, priorGLP1 } =
    getSurveyAnswers();

  return {
    bmi: getBMI(),
    highestBMI: getHighestBMI(),
    previousMedications,
    whichMedication,
    medicalHealthRisks: getMedicalHealthRisks(),
    hrtHealthRisks: getHrtHealthRisks(),
    state: stateOverwrite ?? getMedState(),
    ...(isInsuranceCheckerRoute() && priorGLP1 && { priorGLP1 }),
  };
}

/**
 * Diagnostic companion to getEligibleMedProductsBase: for every SKU that does
 * not pass, lists which checks failed. Used for tracking only.
 */
export function getEligibilityFailuresBySku(
  options: EligibilityCheckOptions & { stateOverwrite?: string },
  flow?: MED_FLOW
): Partial<Record<MED_SKU, EligibilityCheckName[]>> {
  const userInfo = buildEligibilityUserInfo(options.stateOverwrite);
  const failures: Partial<Record<MED_SKU, EligibilityCheckName[]>> = {};

  Object.values(MED_SKU).forEach((sku) => {
    const info = ELIGIBILITY_INFO[sku];
    if (flow && options.checkFlow && info.flow !== flow) {
      return;
    }
    const results = evaluateEligibilityInfo(info, sku, userInfo, options);
    const failed = (Object.keys(results) as EligibilityCheckName[]).filter(
      (name) => !results[name]
    );
    if (failed.length) {
      failures[sku] = failed;
    }
  });

  return failures;
}

export function getEligibleMedProductsBase(
  options: EligibilityCheckOptions & {
    stateOverwrite?: string;
  } = {
    checkBMI: true,
    checkHealthRisks: true,
    checkState: true,
    requirePreviousMedicationsAnswer: true,
    requireHighestBMIAnswer: true,
    checkFlow: true,
  },
  flow?: MED_FLOW
): Array<MED_SKU> {
  const eligibleProducts = [];
  const userInfo = buildEligibilityUserInfo(options.stateOverwrite);

  let eligibleSkus = Object.values(MED_SKU);
  if (flow && options.checkFlow) {
    eligibleSkus = eligibleSkus.filter(
      (sku) => ELIGIBILITY_INFO[sku].flow === flow
    );
  }
  eligibleSkus.forEach((sku) => {
    if (meetsEligibilityInfo(sku, ELIGIBILITY_INFO[sku], userInfo, options)) {
      eligibleProducts.push(sku);
    }
  });

  return eligibleProducts;
}

export const getDefaultEligibilityOptions = (): Parameters<
  typeof getEligibleMedProducts
>[0] => ({
  checkBMI: true,
  checkHealthRisks: true,
  checkState: true,
  checkFlow: true,
  requirePreviousMedicationsAnswer:
    !isHealthRoute() && !isFreemiumHealthRoute(),
  requireHighestBMIAnswer:
    !isHealthRoute() && !isFreemiumHealthRoute() && !isInsuranceCheckerRoute(),
});

/**
 * Wrapper over getEligibleMedProductsBase that adds additional eligibility checks
 * required for health check (age, pregnant, eating disorder).
 */
export function getEligibleMedProductsForHealthCheck(
  options?: Parameters<typeof getEligibleMedProductsBase>[0] & {
    skipEatingDisorderCheck?: boolean;
  }
): MED_SKU[] {
  if (
    !isAgeRangeStatusNoomClinicalEligible() ||
    !isPregnantStatusHealthCheckEligible() ||
    (!options?.skipEatingDisorderCheck &&
      !isEatingDisorderStatusNoomClinicalEligible())
  ) {
    return [];
  }

  return getEligibleMedProductsBase(
    {
      checkBMI: true,
      checkHealthRisks: true,
      checkState: true,
      checkFlow: true,
      requirePreviousMedicationsAnswer: false,
      requireHighestBMIAnswer: false,
      ...options,
    },
    MED_FLOW.CLINICAL
  );
}

export function getEligibleMedProductsForInsuranceChecker(
  options?: Partial<EligibilityCheckOptions> & { stateOverwrite?: string }
): MED_SKU[] {
  return getEligibleMedProductsBase(
    {
      checkBMI: true,
      checkHealthRisks: true,
      checkState: true,
      checkFlow: true,
      requirePreviousMedicationsAnswer: true,
      ...options,
      requireHighestBMIAnswer: false,
      disabledNamedChecks: [
        "isQualifiedForPD",
        "isNotEligibleForCompoundedSemaglutide",
      ] as NamedEligibilityCheck[],
    },
    MED_FLOW.CLINICAL
  );
}

export function getEligibleMedProducts(
  options: EligibilityCheckOptions & {
    stateOverwrite?: string;
    skipEatingDisorderCheck?: boolean;
  } = getDefaultEligibilityOptions(),
  flow?: MED_FLOW
): Array<MED_SKU> {
  if (isHealthRoute()) {
    return getEligibleMedProductsForHealthCheck(options);
  }

  if (isInsuranceCheckerRoute()) {
    return getEligibleMedProductsForInsuranceChecker(options);
  }

  const eligibleProducts = getEligibleMedProductsBase(options, flow);

  // For freemium upsell health route we only want to show the proactive health plan
  if (isFreemiumHealthRoute()) {
    return eligibleProducts.filter(
      (sku) => sku === MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER
    );
  }

  return eligibleProducts;
}

/**
 * Checks if the eligible products include full-dose GLP-1 medications.
 * Full-dose GLP-1 includes: COMPOUNDED, COMPOUNDED_TIRZEPATIDE, GENERIC_LIRAGLUTIDE, and COMPOUNDED_MICRODOSE.
 *
 * @param eligibleProducts - Array of eligible MED_SKU products
 * @returns true if eligibleProducts includes COMPOUNDED, COMPOUNDED_TIRZEPATIDE, GENERIC_LIRAGLUTIDE, or COMPOUNDED_MICRODOSE
 */
export function isFulldoseGlp1Eligible(
  eligibleProducts: Array<MED_SKU>
): boolean {
  return (
    eligibleProducts.includes(MED_SKU.COMPOUNDED) ||
    eligibleProducts.includes(MED_SKU.COMPOUNDED_TIRZEPATIDE) ||
    eligibleProducts.includes(MED_SKU.GENERIC_LIRAGLUTIDE) ||
    eligibleProducts.includes(MED_SKU.COMPOUNDED_MICRODOSE) ||
    eligibleProducts.includes(MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE)
  );
}

/**
 * Checks if the eligible products include compounded microdose semaglutide
 * or compounded microdose tirzepatide.
 */
export function isCompoundedMicrodoseEligible(
  eligibleProducts: Array<MED_SKU>
): boolean {
  return (
    eligibleProducts.includes(MED_SKU.COMPOUNDED_MICRODOSE) ||
    eligibleProducts.includes(MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE)
  );
}

export const getMedFlowByRoute = () => {
  if (isHRTRoute()) {
    return MED_FLOW.HRT;
  }
  if (isHealthRoute()) {
    return MED_FLOW.HEALTH;
  }
  return MED_FLOW.CLINICAL;
};

/**
 * Returns the eligible med tiers based on the recommended med tier.
 * The order is used to determine the visibility of the med tiers in the plan selection page.
 *
 * @param recommendedMedTier - The recommended med tier.
 * @returns The eligible med tiers.
 */
export function getEligibleMedProductsPlanSelection(
  recommendedMedTier: MED_SKU,
  eligibleMedProducts: Array<MED_SKU>
): Array<MED_SKU> {
  let medTiers: Array<MED_SKU> = [];
  switch (recommendedMedTier) {
    case MED_SKU.ORALS_METFORMIN:
      medTiers = [
        MED_SKU.ORALS_METFORMIN,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
        MED_SKU.COMPOUNDED,
        MED_SKU.COMPOUNDED_TIRZEPATIDE,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
        MED_SKU.BRANDED,
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
        MED_SKU.BRANDED_WEGOVY_PILL,
      ];
      break;
    case MED_SKU.BRANDED:
      medTiers = [
        MED_SKU.BRANDED,
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
        MED_SKU.BRANDED_WEGOVY_PILL,
        MED_SKU.COMPOUNDED,
        MED_SKU.COMPOUNDED_TIRZEPATIDE,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
        MED_SKU.ORALS_METFORMIN,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
      ];
      break;
    case MED_SKU.BRANDED_ZEPBOUND_VIAL:
      medTiers = [
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
        MED_SKU.COMPOUNDED_TIRZEPATIDE,
        MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
        MED_SKU.BRANDED_WEGOVY_PILL,
        MED_SKU.BRANDED,
        MED_SKU.COMPOUNDED,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.ORALS_METFORMIN,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
      ];
      break;
    case MED_SKU.BRANDED_WEGOVY_PILL:
      medTiers = [
        MED_SKU.BRANDED_WEGOVY_PILL,
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
        MED_SKU.BRANDED,
        MED_SKU.COMPOUNDED,
        MED_SKU.COMPOUNDED_TIRZEPATIDE,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.ORALS_METFORMIN,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
      ];
      break;
    case MED_SKU.COMPOUNDED_MICRODOSE:
      medTiers = [
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
        MED_SKU.BRANDED_WEGOVY_PILL,
        MED_SKU.COMPOUNDED,
        MED_SKU.COMPOUNDED_TIRZEPATIDE,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
        MED_SKU.BRANDED,
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
        MED_SKU.ORALS_METFORMIN,
      ];
      break;
    case MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE:
      medTiers = [
        MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.COMPOUNDED_TIRZEPATIDE,
        MED_SKU.COMPOUNDED,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
        MED_SKU.BRANDED,
        MED_SKU.BRANDED_WEGOVY_PILL,
        MED_SKU.ORALS_METFORMIN,
      ];
      break;
    case MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER:
      medTiers = [
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
        MED_SKU.COMPOUNDED,
        MED_SKU.COMPOUNDED_TIRZEPATIDE,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.BRANDED,
        MED_SKU.ORALS_METFORMIN,
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
        MED_SKU.BRANDED_WEGOVY_PILL,
      ];
      break;
    case MED_SKU.COMPOUNDED_TIRZEPATIDE:
      medTiers = [
        MED_SKU.COMPOUNDED_TIRZEPATIDE,
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
        MED_SKU.COMPOUNDED,
        MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
        MED_SKU.BRANDED,
        MED_SKU.BRANDED_WEGOVY_PILL,
        MED_SKU.ORALS_METFORMIN,
      ];
      break;
    default:
      medTiers = [
        MED_SKU.COMPOUNDED,
        MED_SKU.COMPOUNDED_TIRZEPATIDE,
        MED_SKU.GENERIC_LIRAGLUTIDE,
        MED_SKU.COMPOUNDED_MICRODOSE,
        MED_SKU.COMPOUNDED_MICRODOSE_TIRZEPATIDE,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
        MED_SKU.BRANDED,
        MED_SKU.BRANDED_ZEPBOUND_VIAL,
        MED_SKU.BRANDED_WEGOVY_PILL,
        MED_SKU.ORALS_METFORMIN,
      ];
      break;
  }
  return medTiers.filter((medTier) => eligibleMedProducts.includes(medTier));
}

export const MED_DISCLOSURE_LINKS = {
  [MED_FLOW.HRT]:
    "https://www.noom.com/static/Safety-Information-for-HRT-Medications.pdf",
  [MED_FLOW.CLINICAL]:
    "https://www.noom.com/static/Safety-Information-for-Certain-Weight-Loss-Medications.pdf",
};

type SKU_INFO = {
  isGlp1: boolean;
};

export const MED_SKU_INFO: Record<MED_SKU, SKU_INFO> = {
  BRANDED: {
    isGlp1: true,
  },
  BRANDED_ZEPBOUND_VIAL: {
    isGlp1: true,
  },
  BRANDED_WEGOVY_PILL: {
    isGlp1: true,
  },
  COMPOUNDED: {
    isGlp1: true,
  },
  COMPOUNDED_TIRZEPATIDE: {
    isGlp1: true,
  },
  COMPOUNDED_MICRODOSE: {
    isGlp1: true,
  },
  COMPOUNDED_MICRODOSE_TIRZEPATIDE: {
    isGlp1: true,
  },
  COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER: {
    isGlp1: true,
  },
  GENERIC_LIRAGLUTIDE: {
    isGlp1: true,
  },
  HRT_CREAM: {
    isGlp1: false,
  },
  HRT_PATCH: {
    isGlp1: false,
  },
  ORALS_METFORMIN: {
    isGlp1: false,
  },
};

export function getEligibleGlp1Products(
  options?: Partial<Parameters<typeof getEligibleMedProducts>[0]>
) {
  return getEligibleMedProducts({
    ...getDefaultEligibilityOptions(),
    ...options,
  }).filter((eligibleMedProduct) => MED_SKU_INFO[eligibleMedProduct].isGlp1);
}
